import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
    LayoutDashboard,
    Home,
    ChevronDown,
    LogOut,
    Menu,
    X,
    Image as ImageIcon,
    CheckCircle,
    CheckCircle as CheckCircleIcon,
    CheckCircle2,

    Package,
    Calculator,
    MessageSquare,
    User,
    ClipboardCheck,
    Bell,
    Building2,
    Key,
    TriangleAlert,
    FileText,
    List,
    Star,
    DollarSign,
    Megaphone,
    HelpCircle,
    Percent,
    File as FileIcon,
    Users as UserGroupIcon,
    Settings,
    Shield,
    BadgeCheck,
    Briefcase,
    CreditCard,
    Table,
    Gavel,
    Award,
    Sparkles as SparklesIcon,
    Info,
    Heart,
    Film as FilmIcon,
    TrendingUp,
    GraduationCap as AcademicCapIcon,
    Camera as PhotoIcon,
    Users as UsersIcon,
    MapPin,
    PlusCircle,
    Globe
} from 'lucide-react';
import logo from '../../img/Aham_logo.png';
import toast from 'react-hot-toast';

const AdminLayout = () => {
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [expandedMenus, setExpandedMenus] = useState(() => {
        const saved = localStorage.getItem('adminExpandedMenus');
        return saved ? JSON.parse(saved) : {};
    });
    const [gmbBranches, setGmbBranches] = useState([]);

    const navRef = useRef(null);
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    // Persist expansion state
    useEffect(() => {
        localStorage.setItem('adminExpandedMenus', JSON.stringify(expandedMenus));
    }, [expandedMenus]);

    // Fetch GMB branches for dynamic sidebar
    useEffect(() => {
        const fetchGMBBranches = async () => {
            try {
                const res = await axios.get('/api/gmb-branches');
                if (res.data.success) {
                    setGmbBranches(res.data.data);
                }
            } catch (err) {
                console.log('GMB branches not loaded:', err.message);
            }
        };
        fetchGMBBranches();
    }, []);

    // Restore scroll position
    useEffect(() => {
        const savedScroll = sessionStorage.getItem('adminSidebarScroll');
        if (savedScroll && navRef.current) {
            navRef.current.scrollTop = parseInt(savedScroll, 10);
        }
    }, []);

    const handleScroll = (e) => {
        sessionStorage.setItem('adminSidebarScroll', e.target.scrollTop);
    };

    const toggleMenu = (menuName, parentName = null) => {
        setExpandedMenus(prev => {
            const isOpening = !prev[menuName];
            if (isOpening) {
                // Accordion logic: opening a top-level menu closes all other top-level menus.
                // If it's a sub-menu (has parentName), only keep that parent and the new menu.
                if (parentName) {
                    return { [parentName]: true, [menuName]: true };
                }
                // Top-level menu: close everything else
                return { [menuName]: true };
            }
            // If closing, just remove it from state
            const newState = { ...prev };
            delete newState[menuName];
            return newState;
        });
    };

    const handleLogout = () => {
        logout();
        toast.success('Logged out successfully');
        navigate('/admin');
    };

    const navItems = [
        {
            name: 'Dashboard',
            path: '/admin/dashboard',
            icon: <LayoutDashboard size={20} />,
            permission: 'dashboard'
        },
        {
            name: 'Home',
            icon: <Home size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Home'],
            toggle: () => toggleMenu('Home'),
            children: [
                { name: 'Banners', path: '/admin/banners', icon: <ImageIcon size={18} />, permission: 'home_banners' },
                { name: 'Why Choose Us', path: '/admin/why-choose', icon: <CheckCircle size={18} />, permission: 'home_why_choose' },
                { name: 'Products', path: '/admin/products', icon: <Package size={18} />, permission: 'home_products' },
                { name: 'EMI Calculator', path: '/admin/dashboard/emi-calculator', icon: <Calculator size={18} />, permission: 'home_emi' },
                { name: 'Scorecard', path: '/admin/scorecard', icon: <ClipboardCheck size={18} />, permission: 'home_scorecard' },
                { name: 'Testimonials', path: '/admin/testimonials', icon: <MessageSquare size={18} />, permission: 'home_testimonials' },
                { name: 'Important News', path: '/admin/important-news', icon: <Bell size={18} />, permission: 'home_news' }
            ]
        },
        {
            name: 'Scorecard',
            icon: <ClipboardCheck size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Scorecard'],
            toggle: () => toggleMenu('Scorecard'),
            children: [
                { name: 'Banner', path: '/admin/scorecard/banner', icon: <ImageIcon size={18} />, permission: 'home_scorecard' },
                { name: 'Scorecard Section', path: '/admin/scorecard/scorecard-section', icon: <ClipboardCheck size={18} />, permission: 'home_scorecard' },
                { name: 'Eligibility Cards', path: '/admin/scorecard/eligibility-cards', icon: <ClipboardCheck size={18} />, permission: 'home_scorecard' },
                { name: 'Max Loan', path: '/admin/scorecard/max-loan', icon: <Building2 size={18} />, permission: 'home_scorecard' },
                { name: 'Eligibility Factors', path: '/admin/scorecard/eligibility-factors', icon: <CheckCircle size={18} />, permission: 'home_scorecard' },
                { name: 'Eligibility Tips', path: '/admin/scorecard/eligibility-tips', icon: <CheckCircle size={18} />, permission: 'home_scorecard' },
                { name: 'Beyond Numbers', path: '/admin/scorecard/beyond-numbers', icon: <CheckCircle size={18} />, permission: 'home_scorecard' },
                { name: 'Why Choose Section', path: '/admin/scorecard/why-choose-section', icon: <CheckCircle size={18} />, permission: 'home_scorecard' },
                { name: 'FAQ Management', path: '/admin/scorecard/faq-management', icon: <CheckCircle size={18} />, permission: 'home_scorecard' },
                { name: 'Products Section', path: '/admin/scorecard/products-section', icon: <Package size={18} />, permission: 'home_scorecard' }
            ]
        },
        {
            name: 'Loans',
            icon: <Building2 size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Loans'],
            toggle: () => toggleMenu('Loans'),
            children: [
                {
                    name: 'Construction Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Construction Loan'],
                    toggle: () => toggleMenu('Construction Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/home-construction-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_construction' },
                        { name: 'Overview', path: '/admin/home-construction-loan/overview', icon: <FileText size={18} />, permission: 'loan_construction' },
                        { name: 'Key Benefits', path: '/admin/home-construction-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_construction' },
                        { name: 'Eligibility Criteria', path: '/admin/home-construction-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_construction' },
                        { name: 'Documents Required', path: '/admin/home-construction-loan/documents', icon: <FileText size={18} />, permission: 'loan_construction' },
                        { name: 'Interest Rates', path: '/admin/home-construction-loan/interest-rates', icon: <Star size={18} />, permission: 'loan_construction' },
                        { name: 'FAQs', path: '/admin/home-construction-loan/faqs', icon: <FileText size={18} />, permission: 'loan_construction' },
                        { name: 'CTA Banner', path: '/admin/home-construction-loan/cta-banner', icon: <Star size={18} />, permission: 'loan_construction' }
                    ]
                },
                {
                    name: 'Home Purchase Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Purchase Loan'],
                    toggle: () => toggleMenu('Purchase Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/home-purchase-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_purchase' },
                        { name: 'Overview', path: '/admin/home-purchase-loan/overview', icon: <FileText size={18} />, permission: 'loan_purchase' },
                        { name: 'Key Benefits', path: '/admin/home-purchase-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_purchase' },
                        { name: 'Eligibility Criteria', path: '/admin/home-purchase-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_purchase' },
                        { name: 'Documents Required', path: '/admin/home-purchase-loan/documents', icon: <FileText size={18} />, permission: 'loan_purchase' },
                        { name: 'Interest Rates', path: '/admin/home-purchase-loan/interest-rates', icon: <FileText size={18} />, permission: 'loan_purchase' },
                        { name: 'FAQs', path: '/admin/home-purchase-loan/faqs', icon: <FileText size={18} />, permission: 'loan_purchase' },
                        { name: 'CTA Banner', path: '/admin/home-purchase-loan/cta-banner', icon: <FileText size={18} />, permission: 'loan_purchase' }
                    ]
                },
                {
                    name: 'Composite Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Composite Loan'],
                    toggle: () => toggleMenu('Composite Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/home-composite-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_composite' },
                        { name: 'Overview', path: '/admin/home-composite-loan/overview', icon: <FileText size={18} />, permission: 'loan_composite' },
                        { name: 'Key Benefits', path: '/admin/home-composite-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_composite' },
                        { name: 'Eligibility Criteria', path: '/admin/home-composite-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_composite' },
                        { name: 'Documents Required', path: '/admin/home-composite-loan/documents', icon: <FileText size={18} />, permission: 'loan_composite' },
                        { name: 'Interest Rates', path: '/admin/home-composite-loan/interest-rates', icon: <DollarSign size={18} />, permission: 'loan_composite' },
                        { name: 'FAQs', path: '/admin/home-composite-loan/faqs', icon: <MessageSquare size={18} />, permission: 'loan_composite' },
                        { name: 'CTA Banner', path: '/admin/home-composite-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_composite' }
                    ]
                },
                {
                    name: ' Renovation Loan',
                    icon: <Home size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Renovation Loan'],
                    toggle: () => toggleMenu('Renovation Loan', 'Loans'),
                    children: [
                        { name: 'Overview', path: '/admin/home-renovation-loan/overview', icon: <FileText size={18} />, permission: 'loan_renovation' },
                        { name: 'Key Benefits', path: '/admin/home-renovation-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_renovation' },
                        { name: 'Eligibility Criteria', path: '/admin/home-renovation-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_renovation' },
                        { name: 'Documents Required', path: '/admin/home-renovation-loan/documents', icon: <FileText size={18} />, permission: 'loan_renovation' },
                        { name: 'Interest Rates', path: '/admin/home-renovation-loan/interest-rates', icon: <DollarSign size={18} />, permission: 'loan_renovation' },
                        { name: 'Banners', path: '/admin/home-renovation-loan/banners', icon: <ImageIcon size={18} />, permission: 'loan_renovation' },
                        { name: 'FAQs', path: '/admin/home-renovation-loan/faqs', icon: <HelpCircle size={18} />, permission: 'loan_renovation' },
                        { name: 'CTA Banner', path: '/admin/home-renovation-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_renovation' }
                    ]
                },
                {
                    name: 'Extension Loan',
                    icon: <Home size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Extension Loan'],
                    toggle: () => toggleMenu('Extension Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/home-extension-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_extension' },
                        { name: 'Overview', path: '/admin/home-extension-loan/overview', icon: <FileText size={18} />, permission: 'loan_extension' },
                        { name: 'Key Benefits', path: '/admin/home-extension-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_extension' },
                        { name: 'Eligibility Criteria', path: '/admin/home-extension-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_extension' },
                        { name: 'Documents Required', path: '/admin/home-extension-loan/documents', icon: <FileText size={18} />, permission: 'loan_extension' },
                        { name: 'Interest Rates', path: '/admin/home-extension-loan/interest-rates', icon: <Percent size={18} />, permission: 'loan_extension' },
                        { name: 'FAQs', path: '/admin/home-extension-loan/faqs', icon: <HelpCircle size={18} />, permission: 'loan_extension' },
                        { name: 'CTA Banner', path: '/admin/home-extension-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_extension' }
                    ]
                },
                {
                    name: 'Balance Transfer Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Balance Transfer Loan'],
                    toggle: () => toggleMenu('Balance Transfer Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/balance-transfer-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'Overview', path: '/admin/balance-transfer-loan/overview', icon: <FileText size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'Key Benefits', path: '/admin/balance-transfer-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'Eligibility Criteria', path: '/admin/balance-transfer-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'Documents Required', path: '/admin/balance-transfer-loan/documents', icon: <FileText size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'Interest Rates', path: '/admin/balance-transfer-loan/interest-rates', icon: <Percent size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'FAQs', path: '/admin/balance-transfer-loan/faqs', icon: <HelpCircle size={18} />, permission: 'loan_balance_transfer' },
                        { name: 'CTA Banner', path: '/admin/balance-transfer-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_balance_transfer' }
                    ]
                },
                {
                    name: 'NRI Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['NRI Loan'],
                    toggle: () => toggleMenu('NRI Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/nri-loan/banner', icon: <ImageIcon size={18} /> },
                        { name: 'Overview', path: '/admin/nri-loan/overview', icon: <FileText size={18} /> },
                        { name: 'Key Benefits', path: '/admin/nri-loan/key-benefits', icon: <Star size={18} /> },
                        { name: 'Eligibility Criteria', path: '/admin/nri-loan/eligibility', icon: <CheckCircle size={18} /> },
                        { name: 'Documents Required', path: '/admin/nri-loan/documents', icon: <FileText size={18} /> },
                        { name: 'Interest Rates', path: '/admin/nri-loan/interest-rates', icon: <Star size={18} /> },
                        { name: 'FAQs', path: '/admin/nri-loan/faqs', icon: <FileText size={18} /> },
                        { name: 'CTA Banner', path: '/admin/nri-loan/cta-banner', icon: <Star size={18} /> }
                    ]
                },
                {
                    name: 'Top-up Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Top-up Loan'],
                    toggle: () => toggleMenu('Top-up Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/top-up-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_topup' },
                        { name: 'Overview', path: '/admin/top-up-loan/overview', icon: <FileText size={18} />, permission: 'loan_topup' },
                        { name: 'Key Benefits', path: '/admin/top-up-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_topup' },
                        { name: 'Eligibility Criteria', path: '/admin/top-up-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_topup' },
                        { name: 'Documents Required', path: '/admin/top-up-loan/documents', icon: <FileIcon size={18} />, permission: 'loan_topup' },
                        { name: 'Interest Rates', path: '/admin/top-up-loan/interest-rates', icon: <Percent size={18} />, permission: 'loan_topup' },
                        { name: 'FAQs', path: '/admin/top-up-loan/faqs', icon: <HelpCircle size={18} />, permission: 'loan_topup' },
                        { name: 'CTA Banner', path: '/admin/top-up-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_topup' }
                    ]
                },
                {
                    name: 'Mortgage Loan',
                    icon: <Building2 size={20} />,
                    isDropdown: true,
                    isOpen: expandedMenus['Mortgage Loan'],
                    toggle: () => toggleMenu('Mortgage Loan', 'Loans'),
                    children: [
                        { name: 'Banner', path: '/admin/mortgage-loan/banner', icon: <ImageIcon size={18} />, permission: 'loan_mortgage' },
                        { name: 'Overview', path: '/admin/mortgage-loan/overview', icon: <FileText size={18} />, permission: 'loan_mortgage' },
                        { name: 'Key Benefits', path: '/admin/mortgage-loan/key-benefits', icon: <Star size={18} />, permission: 'loan_mortgage' },
                        { name: 'Eligibility Criteria', path: '/admin/mortgage-loan/eligibility', icon: <CheckCircle size={18} />, permission: 'loan_mortgage' },
                        { name: 'Documents Required', path: '/admin/mortgage-loan/documents', icon: <FileIcon size={18} />, permission: 'loan_mortgage' },
                        { name: 'Interest Rates', path: '/admin/mortgage-loan/interest-rates', icon: <Percent size={18} />, permission: 'loan_mortgage' },
                        { name: 'FAQs', path: '/admin/mortgage-loan/faqs', icon: <HelpCircle size={18} />, permission: 'loan_mortgage' },
                        { name: 'CTA Banner', path: '/admin/mortgage-loan/cta-banner', icon: <Megaphone size={18} />, permission: 'loan_mortgage' }
                    ]
                },
            ]
        },

        {
            name: 'PMAY',
            icon: <Building2 size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['PMAY'],
            toggle: () => toggleMenu('PMAY'),
            children: [
                { name: 'Banner', path: '/admin/pmay/banner', icon: <ImageIcon size={18} />, permission: 'pmay' },
                { name: 'Overview', path: '/admin/pmay/overview', icon: <FileText size={18} />, permission: 'pmay' },
                { name: 'Eligibility', path: '/admin/pmay/eligibility', icon: <BadgeCheck size={18} />, permission: 'pmay' }
            ]
        },
        {
            name: 'Partner Us',
            icon: <UserGroupIcon size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Partner Us'],
            toggle: () => toggleMenu('Partner Us'),
            children: [
                { name: 'Banner', path: '/admin/partner-us/banner', icon: <ImageIcon size={18} />, permission: 'partner_us' },
                { name: 'Overview', path: '/admin/partner-us/overview', icon: <FileText size={18} />, permission: 'partner_us' },
                { name: 'Why Partner with Us', path: '/admin/partner-us/benefits', icon: <Star size={18} />, permission: 'partner_us' },
                { name: 'Who Can Join', path: '/admin/partner-us/who-can-join', icon: <UserGroupIcon size={18} />, permission: 'partner_us' },
                { name: 'How It Works', path: '/admin/partner-us/how-it-works', icon: <Settings size={18} />, permission: 'partner_us' },
                { name: 'Support & Training', path: '/admin/partner-us/support-training', icon: <CheckCircle size={18} />, permission: 'partner_us' },
                { name: 'Success Stories', path: '/admin/partner-us/success-stories', icon: <Star size={18} />, permission: 'partner_us' },
                { name: 'Registration Requirements', path: '/admin/partner-us/registration-requirements', icon: <ClipboardCheck size={18} />, permission: 'partner_us' },
                { name: 'FAQs', path: '/admin/partner-us/faqs', icon: <HelpCircle size={18} />, permission: 'partner_us' },
                { name: 'High Demand Products', path: '/admin/partner-us/high-demand-products', icon: <Package size={18} />, permission: 'partner_us' },
                { name: 'CTA Banner', path: '/admin/partner-us/cta-banner', icon: <Megaphone size={18} />, permission: 'partner_us' }
            ]
        },

        {
            name: 'DSA Onboarding',
            icon: <Briefcase size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['DSA Onboarding'],
            toggle: () => toggleMenu('DSA Onboarding'),
            children: [
                { name: 'Banner', path: '/admin/dsa-onboarding/banner', icon: <ImageIcon size={18} />, permission: 'dsa_onboarding' },
                { name: 'Why Choose Aham', path: '/admin/dsa-onboarding/benefits', icon: <Star size={18} />, permission: 'dsa_onboarding' },
                { name: 'Getting Started', path: '/admin/dsa-onboarding/steps', icon: <CheckCircle2 size={18} />, permission: 'dsa_onboarding' },
                { name: 'Requirements', path: '/admin/dsa-onboarding/requirements', icon: <FileText size={18} />, permission: 'dsa_onboarding' },
                { name: 'Bottom CTA', path: '/admin/dsa-onboarding/cta', icon: <Megaphone size={18} />, permission: 'dsa_onboarding' },
            ]
        },

        {
            name: 'Governance',
            icon: <Shield size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Governance'],
            toggle: () => toggleMenu('Governance'),
            children: [
                { name: 'Banner', path: '/admin/governance/banner', icon: <ImageIcon size={18} />, permission: 'governance' },
                { name: 'Foundation Settings', path: '/admin/governance/foundation', icon: <Settings size={18} />, permission: 'governance' },
                { name: 'Transparency Settings', path: '/admin/governance/transparency', icon: <Settings size={18} />, permission: 'governance' },
                { name: 'Oversight Settings', path: '/admin/governance/oversight', icon: <Settings size={18} />, permission: 'governance' },
                { name: 'Ethics Settings', path: '/admin/governance/ethics', icon: <Settings size={18} />, permission: 'governance' },
                { name: 'CTA Banner', path: '/admin/governance/cta-banner', icon: <Megaphone size={18} />, permission: 'governance' }
            ]
        },
        {
            name: 'Grievances',
            icon: <MessageSquare size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Grievances'],
            toggle: () => toggleMenu('Grievances'),
            children: [
                { name: 'Banner', path: '/admin/grievance-redressal/banner', icon: <ImageIcon size={18} />, permission: 'grievances' },
                { name: 'Resolution Process', path: '/admin/grievance-redressal/steps', icon: <CheckCircle2 size={18} />, permission: 'grievances' },
                { name: 'Complaint Methods', path: '/admin/grievance-redressal/methods', icon: <MessageSquare size={18} />, permission: 'grievances' },
                { name: 'Level 2 Escalation', path: '/admin/grievance-redressal/level2', icon: <Building2 size={18} />, permission: 'grievances' },
                { name: 'Level 3 Escalation', path: '/admin/grievance-redressal/level3', icon: <Shield size={18} />, permission: 'grievances' },
                { name: 'Commitment & Rights', path: '/admin/grievance-redressal/commitment', icon: <Shield size={18} />, permission: 'grievances' }
            ]
        },
        {
            name: 'Risk Classification',
            icon: <Shield size={20} />, // Using Shield icon
            isDropdown: true,
            isOpen: expandedMenus['Risk Classification'],
            toggle: () => toggleMenu('Risk Classification'),
            children: [
                { name: 'Banner', path: '/admin/risk-classification/banner', icon: <ImageIcon size={18} />, permission: 'risk_classification' },
                { name: 'Summary Cards', path: '/admin/risk-classification/cards', icon: <CreditCard size={18} />, permission: 'risk_classification' },
                { name: 'Table Data', path: '/admin/risk-classification/table-data', icon: <Table size={18} />, permission: 'risk_classification' }
            ]
        },
        {
            name: 'Rating Certificate',
            icon: <Award size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Rating Certificate'],
            toggle: () => toggleMenu('Rating Certificate'),
            children: [
                { name: 'Banner', path: '/admin/rating-certificate/banner', icon: <ImageIcon size={18} />, permission: 'rating_certificate' },
                { name: 'Understanding Value', path: '/admin/rating-certificate/value', icon: <CheckCircleIcon size={18} />, permission: 'rating_certificate' },
                { name: 'Credentials', path: '/admin/rating-certificate/credentials', icon: <BadgeCheck size={18} />, permission: 'rating_certificate' },
                { name: 'Behind the Scenes', path: '/admin/rating-certificate/behind-scenes', icon: <Shield size={18} />, permission: 'rating_certificate' },
                { name: 'Your Advantage', path: '/admin/rating-certificate/advantage', icon: <Star size={18} />, permission: 'rating_certificate' },
                { name: 'Our Pledge', path: '/admin/rating-certificate/pledge', icon: <CheckCircleIcon size={18} />, permission: 'rating_certificate' },
                { name: 'Assessments', path: '/admin/rating-certificate/assessments', icon: <BadgeCheck size={18} />, permission: 'rating_certificate' },
                //{ name: 'CTA Banner', path: '/admin/rating-certificate/cta', icon: <SparklesIcon size={18} />, permission: 'rating_certificate' }
            ]
        },
        {
            name: 'Property for Sale',
            icon: <Gavel size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Property for Sale'],
            toggle: () => toggleMenu('Property for Sale'),
            children: [
                { name: 'Banner', path: '/admin/e-auction/banner', icon: <ImageIcon size={18} />, permission: 'e_auction' },
                { name: 'Understanding Process', path: '/admin/e-auction/process', icon: <FileText size={18} />, permission: 'e_auction' },
                { name: 'Property Listings', path: '/admin/e-auction/properties', icon: <Building2 size={18} />, permission: 'e_auction' },
                { name: 'Getting Started', path: '/admin/e-auction/getting-started', icon: <CheckCircle2 size={18} />, permission: 'e_auction' },
                { name: 'Legal Information', path: '/admin/e-auction/legal-information', icon: <Shield size={18} />, permission: 'e_auction' }
            ]

        },
        {
            name: 'Secured Assets',
            icon: <Shield size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Secured Assets'],
            toggle: () => toggleMenu('Secured Assets'),
            children: [
                { name: 'Banner', path: '/admin/secured-assets/banner', icon: <ImageIcon size={18} />, permission: 'secured_assets' },
                { name: 'Core Concept', path: '/admin/secured-assets/core-concept', icon: <Info size={18} />, permission: 'secured_assets' },
                { name: 'What We Finance', path: '/admin/secured-assets/finance', icon: <Building2 size={18} />, permission: 'secured_assets' },
                { name: 'Know Your Terms', path: '/admin/secured-assets/terms', icon: <Key size={18} />, permission: 'secured_assets' },
                { name: 'Transparency', path: '/admin/secured-assets/transparency', icon: <TriangleAlert size={18} />, permission: 'secured_assets' },
                { name: 'Release Process', path: '/admin/secured-assets/release', icon: <Award size={18} />, permission: 'secured_assets' },
                { name: 'Table Data', path: '/admin/secured-assets/table-data', icon: <Table size={18} />, permission: 'secured_assets' },
                //{ name: 'CTA Banner', path: '/admin/secured-assets/cta-banner', icon: <Megaphone size={18} />, permission: 'secured_assets' }
            ]
        },
        {
            name: 'Life at Aham',
            icon: <Heart size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Life at Aham'],
            toggle: () => toggleMenu('Life at Aham'),
            children: [
                { name: 'Banner', path: '/admin/life-at-aham/banner', icon: <ImageIcon size={18} />, permission: 'life_at_aham' },
                { name: 'Our Culture', path: '/admin/life-at-aham/culture', icon: <Heart size={18} />, permission: 'life_at_aham' },
                { name: 'Where We Thrive', path: '/admin/life-at-aham/thrive', icon: <SparklesIcon size={18} />, permission: 'life_at_aham' },
                { name: 'Your Journey', path: '/admin/life-at-aham/journey', icon: <TrendingUp size={18} />, permission: 'life_at_aham' },
                { name: 'Life Beyond Work', path: '/admin/life-at-aham/beyond', icon: <CheckCircle size={18} />, permission: 'life_at_aham' },
                { name: 'Recognition & Excellence', path: '/admin/life-at-aham/recognition', icon: <Award size={18} />, permission: 'life_at_aham' },
                { name: 'CTA Banner', path: '/admin/life-at-aham/cta-banner', icon: <SparklesIcon size={18} />, permission: 'life_at_aham' }
            ]
        },
        {
            name: 'Gallery ',
            icon: <ImageIcon size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Gallery'],
            toggle: () => toggleMenu('Gallery'),
            children: [
                { name: 'Banners', path: '/admin/gallery/banners', icon: <ImageIcon size={18} />, permission: 'gallery' },
                { name: 'Work Images', path: '/admin/gallery/work-images', icon: <PhotoIcon size={18} />, permission: 'gallery' },
                { name: 'Celebration Images', path: '/admin/gallery/celebration-images', icon: <SparklesIcon size={18} />, permission: 'gallery' },
                { name: 'Growth Images', path: '/admin/gallery/growth-images', icon: <AcademicCapIcon size={18} />, permission: 'gallery' },
                { name: 'CTA Banner', path: '/admin/gallery/cta', icon: <Megaphone size={18} />, permission: 'gallery' }
            ]
        },
        {
            name: 'Career',
            icon: <Briefcase size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Career'],
            toggle: () => toggleMenu('Career'),
            children: [
                { name: 'Banner', path: '/admin/career/banner', icon: <ImageIcon size={18} />, permission: 'career' }
            ]
        },
        {
            name: 'About Aham',
            icon: <Info size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['About Aham'],
            toggle: () => toggleMenu('About Aham'),
            children: [
                { name: 'Banner', path: '/admin/about-aham/banner', icon: <ImageIcon size={18} />, permission: 'about_aham' },
                { name: 'Overview', path: '/admin/about-aham/overview', icon: <FileText size={18} />, permission: 'about_aham' },
                { name: 'Vision & Mission', path: '/admin/about-aham/vision-mission', icon: <Star size={18} />, permission: 'about_aham' },
                { name: 'Core Values', path: '/admin/about-aham/core-values', icon: <Heart size={18} />, permission: 'about_aham' },
                { name: 'Watch Journey', path: '/admin/about-aham/journey', icon: <FilmIcon size={18} />, permission: 'about_aham' }
            ]
        },
        {
            name: 'Partnership',
            icon: <Briefcase size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Partnership'],
            toggle: () => toggleMenu('Partnership'),
            children: [
                { name: 'Banner', path: '/admin/partnership/banner', icon: <ImageIcon size={18} />, permission: 'partnership' },
                { name: 'Management Team', path: '/admin/leadership/active', icon: <UserGroupIcon size={18} />, permission: 'partnership' }
            ]
        },


        {
            name: 'Investor Info',
            icon: <Building2 size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Investor Info'],
            toggle: () => toggleMenu('Investor Info'),
            children: [
                { name: 'Banner', path: '/admin/investor-info/banner', icon: <ImageIcon size={18} />, permission: 'investor_info' },
                { name: 'Foundation Settings', path: '/admin/investor-info/foundation-settings', icon: <Settings size={18} />, permission: 'investor_info' },
                { name: 'Vision Points', path: '/admin/investor-info/vision-points', icon: <Star size={18} />, permission: 'investor_info' },
                { name: 'Progress Settings', path: '/admin/investor-info/progress-settings', icon: <Settings size={18} />, permission: 'investor_info' },
                { name: 'Progress Highlights', path: '/admin/investor-info/progress-highlights', icon: <CheckCircle2 size={18} />, permission: 'investor_info' },
                { name: 'Trust Settings', path: '/admin/investor-info/trust-settings', icon: <Settings size={18} />, permission: 'investor_info' },
                { name: 'Governance Highlights', path: '/admin/investor-info/governance-highlights', icon: <Shield size={18} />, permission: 'investor_info' },
                //{ name: 'CTA Banner', path: '/admin/investor-info/cta-banner', icon: <Megaphone size={18} />, permission: 'investor_info' }
            ]
        },
        // {
        //     title: 'Gallery',
        //     items: [
        //         { name: 'Work', path: '/admin/gallery/work', icon: <Briefcase size={18} /> },
        //         { name: 'Celebrations', path: '/admin/gallery/celebration', icon: <SparklesIcon size={18} /> },
        //         { name: 'Growth', path: '/admin/gallery/growth', icon: <TrendingUp size={18} /> },
        //         { name: 'CTA Banner', path: '/admin/gallery/cta', icon: <Megaphone size={18} /> }
        //     ]
        // },

        // this is unwanted but dont delete it
        // {
        //     name: 'Our Team',
        //     icon: <UserGroupIcon size={20} />,
        //     isDropdown: true,
        //     isOpen: isOurTeamExpanded,
        //     toggle: () => setIsOurTeamExpanded(!isOurTeamExpanded),
        //     children: [
        //         { name: 'Banner', path: '/admin/our-team/banner', icon: <ImageIcon size={18} /> },
        //         { name: 'Management Team', path: '/admin/our-team/members', icon: <UserGroupIcon size={18} /> }
        //     ]
        // },
        {
            name: 'FAQ',
            icon: <HelpCircle size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['FAQ'],
            toggle: () => toggleMenu('FAQ'),
            children: [
                { name: 'Banner', path: '/admin/faq/banner', icon: <ImageIcon size={18} />, permission: 'faq' },
                { name: 'Management', path: '/admin/faq/management', icon: <Settings size={18} />, permission: 'faq' },
                { name: 'Rate Matrix', path: '/admin/faq/rate-matrix', icon: <Table size={18} />, permission: 'faq' },
                { name: 'Deviations', path: '/admin/faq/deviations', icon: <List size={18} />, permission: 'faq' }
            ]
        },


        {
            name: 'Branch Locator',
            icon: <MapPin size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Branch Locator'],
            toggle: () => toggleMenu('Branch Locator'),
            children: [
                { name: 'Banner', path: '/admin/branch-locator/banner', icon: <ImageIcon size={18} />, permission: 'branch_locator' },
                { name: 'Branch Management', path: '/admin/branch-locator/management', icon: <Building2 size={18} />, permission: 'branch_locator' },
                { name: 'Notice Settings', path: '/admin/branch-locator/notice', icon: <Settings size={18} />, permission: 'branch_locator' }
            ]
        },
        {
            name: 'GMB Branch Pages',
            icon: <Globe size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['GMB Branch Pages'],
            toggle: () => toggleMenu('GMB Branch Pages'),
            children: [
                { name: '📋 Manage All', path: '/admin/gmb-branch-pages', icon: <Settings size={18} />, permission: 'branch_locator' },
               // { name: '+ Create New Page', path: '/admin/gmb-branch-pages', icon: <PlusCircle size={18} />, permission: 'branch_locator' },
                ...gmbBranches.map(branch => ({
                    name: `${branch.branch_name} Branch`,
                    icon: <MapPin size={18} />,
                    isDropdown: true,
                    isOpen: expandedMenus[`GMB_${branch.branch_name}`],
                    toggle: () => toggleMenu(`GMB_${branch.branch_name}`, 'GMB Branch Pages'),
                    children: [
                        { name: 'Hero Section', path: `/admin/gmb-branch-pages/${branch.id}/sections/hero`, icon: <ImageIcon size={16} />, permission: 'branch_locator' },
                        { name: 'Features Strip', path: `/admin/gmb-branch-pages/${branch.id}/sections/features_strip`, icon: <CheckCircle size={16} />, permission: 'branch_locator' },
                        { name: 'YouTube Videos', path: `/admin/gmb-branch-pages/${branch.id}/sections/youtube_videos`, icon: <FilmIcon size={16} />, permission: 'branch_locator' },
                        { name: 'Why Choose', path: `/admin/gmb-branch-pages/${branch.id}/sections/why_choose`, icon: <Star size={16} />, permission: 'branch_locator' },
                        { name: 'Local Insights', path: `/admin/gmb-branch-pages/${branch.id}/sections/local_insights`, icon: <Info size={16} />, permission: 'branch_locator' },
                        { name: 'Loan Products', path: `/admin/gmb-branch-pages/${branch.id}/sections/loan_products`, icon: <Building2 size={16} />, permission: 'branch_locator' },
                        { name: 'How To Apply', path: `/admin/gmb-branch-pages/${branch.id}/sections/how_to_apply`, icon: <List size={16} />, permission: 'branch_locator' },
                        { name: 'Eligibility', path: `/admin/gmb-branch-pages/${branch.id}/sections/eligibility`, icon: <CheckCircle size={16} />, permission: 'branch_locator' },
                        { name: 'Customer Stories', path: `/admin/gmb-branch-pages/${branch.id}/sections/customer_stories`, icon: <MessageSquare size={16} />, permission: 'branch_locator' },
                        { name: 'Visit Us / Map', path: `/admin/gmb-branch-pages/${branch.id}/sections/visit_us`, icon: <MapPin size={16} />, permission: 'branch_locator' },
                        { name: 'Gallery', path: `/admin/gmb-branch-pages/${branch.id}/sections/gallery`, icon: <ImageIcon size={16} />, permission: 'branch_locator' },
                        { name: 'Coverage Areas', path: `/admin/gmb-branch-pages/${branch.id}/sections/coverage`, icon: <MapPin size={16} />, permission: 'branch_locator' },
                        { name: 'FAQ Section', path: `/admin/gmb-branch-pages/${branch.id}/sections/faq`, icon: <HelpCircle size={16} />, permission: 'branch_locator' },
                        { name: 'CTA Section', path: `/admin/gmb-branch-pages/${branch.id}/sections/cta`, icon: <Megaphone size={16} />, permission: 'branch_locator' },
                        { name: 'SEO Settings', path: `/admin/gmb-branch-pages/${branch.id}/sections/seo`, icon: <Settings size={16} />, permission: 'branch_locator' }
                    ]
                }))
            ]
        },
        {
            name: 'Blog',
            icon: <FileText size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Blog'],
            toggle: () => toggleMenu('Blog'),
            children: [
                { name: 'Banner', path: '/admin/blog/banner', icon: <ImageIcon size={18} />, permission: 'blog' },
                { name: 'Blog Management', path: '/admin/blog/management', icon: <FileText size={18} />, permission: 'blog' },
                //{ name: 'CTA Settings', path: '/admin/blog/cta-settings', icon: <Settings size={18} />, permission: 'blog' }
            ]
        },
        {
            name: 'Festival Campaign',
            icon: <SparklesIcon size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Festival Campaign'],
            toggle: () => toggleMenu('Festival Campaign'),
            children: [
                { name: 'Banner', path: '/admin/festival-campaign/banner', icon: <ImageIcon size={18} /> },
                // { name: 'Contact Settings', path: '/admin/festival-campaign/contact', icon: <Building2 size={18} /> }

            ]
        },

        {
            name: 'Enquiries',
            icon: <MessageSquare size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Enquiries'],
            toggle: () => toggleMenu('Enquiries'),
            children: [
                { name: 'Contact Us', path: '/admin/enquiries/contact', icon: <User size={18} />, permission: 'enquiries_contact' },
                { name: 'Credit Score', path: '/admin/enquiries/credit-score', icon: <CreditCard size={18} />, permission: 'enquiries_credit_score' },
                { name: 'Eligibility', path: '/admin/enquiries/eligibility', icon: <ClipboardCheck size={18} />, permission: 'enquiries_eligibility' },
                { name: 'DSA Partner', path: '/admin/enquiries/dsa-partner', icon: <Briefcase size={18} />, permission: 'enquiries_dsa' },
                { name: 'DSA Partnership', path: '/admin/enquiries/dsa-partnership', icon: <Briefcase size={18} />, permission: 'enquiries_dsa_partnership' },


                { name: 'Auction', path: '/admin/enquiries/auction', icon: <Gavel size={18} />, permission: 'enquiries_auction' },

                { name: 'Grievance', path: '/admin/enquiries/grievance', icon: <MessageSquare size={18} />, permission: 'enquiries_grievance' },
                { name: 'Life at Aham', path: '/admin/enquiries/life-at-aham', icon: <Heart size={18} />, permission: 'enquiries_life_at_aham' }
            ]
        },
        // {
        //     name: 'Resources',
        //     icon: <FileIcon size={20} />,
        //     isDropdown: true,
        //     isOpen: expandedMenus['Resources'],
        //     toggle: () => toggleMenu('Resources'),
        //     children: [
        //         { name: 'Banner', path: '/admin/resources/banner', icon: <ImageIcon size={18} />, permission: 'resources' },
        //         { name: 'Document Management', path: '/admin/resources/management', icon: <FileText size={18} />, permission: 'resources' }
        //     ]
        // },
        {
            name: 'Settings',
            icon: <Settings size={20} />,
            isDropdown: true,
            isOpen: expandedMenus['Settings'],
            toggle: () => toggleMenu('Settings'),
            children: [
                { name: 'System Report', path: '/admin/settings/report', icon: <FileText size={18} />, permission: 'settings_report' },
                { name: 'Add User', path: '/admin/settings/users', icon: <UsersIcon size={18} />, permission: 'settings_users' },
                { name: 'Role Permissions', path: '/admin/settings/permissions', icon: <Shield size={18} />, permission: 'settings_permissions' }
            ]
        }
    ];

    const renderNavItem = (item, index, level = 0) => {
        if (item.isDropdown) {
            return (
                <div key={index} className="space-y-1">
                    <button
                        onClick={item.toggle}
                        className={`w-full flex items-center justify-between px-4 py-3 rounded-xl transition-all duration-200 group ${item.isOpen ? 'bg-slate-50 text-[#283079]' : 'text-slate-600 hover:bg-slate-50'
                            }`}
                    >
                        <div className="flex items-center gap-3">
                            <span className={`${item.isOpen ? 'text-[#283079]' : 'text-slate-400 group-hover:text-slate-600'}`}>
                                {item.icon}
                            </span>
                            <span className="font-semibold">{item.name}</span>
                        </div>
                        <ChevronDown
                            size={16}
                            className={`transition-transform duration-300 ${item.isOpen ? 'rotate-180' : ''}`}
                        />
                    </button>
                    {item.isOpen && (
                        <div className="ml-4 pl-4 space-y-1 pt-1 pb-2 border-l border-slate-100">
                            {item.children.map((child, cIdx) => renderNavItem(child, cIdx, level + 1))}
                        </div>
                    )}
                </div>
            );
        }

        const isRoot = level === 0;
        return (
            <NavLink
                key={index}
                to={item.path}
                className={({ isActive }) =>
                    `flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-200 group ${isRoot
                        ? (isActive ? 'bg-[#283079] text-white shadow-lg shadow-[#283079]/20 font-bold' : 'text-slate-600 hover:bg-slate-50 font-semibold')
                        : (isActive ? 'bg-[#283079]/5 text-[#283079] font-medium' : 'text-slate-500 hover:text-[#283079] hover:bg-slate-50 font-medium')
                    }`
                }
            >
                <span className={({ isActive }) => (isActive ? (isRoot ? 'text-white' : 'text-[#283079]') : 'text-slate-400 group-hover:text-slate-600')}>
                    {item.icon}
                </span>
                {item.name}
            </NavLink>
        );
    };

    return (
        <div className="min-h-screen bg-[#F8FAFC] flex font-inter" >


            {/* Sidebar */}
            <aside
                className={`fixed inset-y-0 left-0 z-50 w-80 bg-white border-r border-slate-200 transition-transform duration-300 ease-in-out transform ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
                    } lg:relative lg:translate-x-0`}
            >
                <div className="h-full flex flex-col">
                    {/* Sidebar Header */}
                    <div className="p-6 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div className="bg-slate-50 p-2 rounded-lg border border-slate-100">
                                <img src={logo} alt="Logo" className="h-8 object-contain" />
                            </div>
                        </div>
                        <button className="lg:hidden text-slate-500" onClick={() => setIsSidebarOpen(false)}>
                            <X size={24} />
                        </button>
                    </div>

                    {/* Navigation */}
                    <nav
                        ref={navRef}
                        onScroll={handleScroll}
                        className="flex-1 px-4 py-4 space-y-2 overflow-y-auto"
                    >
                        {(() => {
                            const hasPermission = (permission) => {
                                // If no permission required, allow
                                if (!permission) return true;
                                // If user not loaded or no permissions, deny (safe default)
                                if (!user || !user.permissions) return false;
                                return user.permissions[permission] === true;
                            };

                            const filterItems = (items) => {
                                return items.reduce((acc, item) => {
                                    // Check permission for the item itself
                                    const isAllowed = hasPermission(item.permission);

                                    // If it has children, filter them
                                    if (item.children) {
                                        const filteredChildren = filterItems(item.children);
                                        // Include parent if it's allowed AND (has distinct permission OR has visible children)
                                        // Actually, if it has children, we usually only show it if at least one child is visible
                                        // UNLESS the parent itself has a link (which these don't seem to, they are groups)

                                        // Logic: If item is a group (isDropdown), show only if it has visible children
                                        if (filteredChildren.length > 0) {
                                            acc.push({ ...item, children: filteredChildren });
                                        }
                                    } else if (isAllowed) {
                                        // Leaf node: show if allowed
                                        acc.push(item);
                                    }

                                    return acc;
                                }, []);
                            };

                            const visibleNavItems = filterItems(navItems);
                            return visibleNavItems.map((item, idx) => renderNavItem(item, idx));
                        })()}
                    </nav>

                    {/* Sidebar Footer */}
                    <div className="p-4 border-t border-slate-100">
                        <button
                            onClick={handleLogout}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-red-500 font-bold hover:bg-red-50 transition-all group"
                        >
                            <LogOut size={20} className="group-hover:-translate-x-1 transition-transform" />
                            Sign Out
                        </button>
                    </div>
                </div>
            </aside >

            {/* Main Content Area */}
            < div className="flex-1 flex flex-col h-screen overflow-hidden" >
                {/* Header */}
                < header className="h-20 bg-white border-b border-slate-200 flex items-center justify-between px-6 z-40" >
                    <div className="flex items-center gap-4">
                        <button className="lg:hidden text-slate-500" onClick={() => setIsSidebarOpen(true)}>
                            <Menu size={24} />
                        </button>
                        <div className="hidden lg:block">
                            <h2 className="text-xl font-bold text-slate-800">Control Panel</h2>
                        </div>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-right hidden sm:block">
                            <p className="text-sm font-bold text-slate-800">Welcome, {user?.username || 'Admin'}</p>
                            <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-tight">System Administrator</p>
                        </div>
                        <div className="w-10 h-10 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-[#283079] font-bold text-lg shadow-sm">
                            {user?.username?.charAt(0).toUpperCase() || 'A'}
                        </div>
                    </div>
                </header >

                {/* Content */}
                < main className="flex-1 overflow-y-auto p-6 md:p-8" >
                    <Outlet />
                </main >
            </div >
        </div >
    );
};

export default AdminLayout;
